import type {TrackingData, TrackingPayload} from "~/types/types";

// Click tracking (TAL-11043). Clicks are queued in memory and posted in batches to
// POST /v2/events/{id}/tracking, never one request per click: every 20 s (plus up to 30 percent jitter so
// a hall of phones does not post in step) while the queue is not empty, and at once when the page is hidden.
// Nothing is persisted: a closed tab loses at most what it had not sent yet.
//
// A click carries only ids, fixed slugs and counts. Never a search term, a URL, a query or anything typed:
// the API drops unknown fields, but the point is that they never leave the phone.
//
// plugins/tracking.ts wires this up: the v-track directive and its document listener, screen views, the
// flush on hide and the reset on logout. Code that tracks the outcome of a request (a bookmark that was
// saved, a connection that was sent) calls trackClick() once it succeeded.

export type TrackingEntity = 'screen' | 'session' | 'speaker' | 'partner' | 'attendee' | 'search' | 'filter'
  | 'menu' | 'tile' | 'notification';
export type TrackingAction = 'view' | 'open' | 'bookmark' | 'unbookmark' | 'connect' | 'submit' | 'apply';

export type TrackingDetails = {
  entityId?: number | null,
  item?: string | null,
  count?: number | null,
  // The event the click belongs to when it is not the one on screen (a notification of another event).
  // null means it has none, and then nothing is tracked.
  eventId?: number | null,
};

type TrackedClick = {
  id: string,
  at: number,
  entity: TrackingEntity,
  action: TrackingAction,
  entity_id?: number,
  item?: string,
  count?: number,
  screen?: string,
  visit: string,
};

// Who and where are fixed when the click happens: moving on to an excluded screen or another event does not
// change where an already queued click goes. The attempt count travels with it, so a retry happens once.
type QueuedClick = {eventId: number, userId: number, click: TrackedClick, attempts: number};

export type TrackingContext = {
  // The signed-in user, or null when logged out: nothing is tracked then.
  userId: () => number | null,
  // The event on screen, or null.
  eventId: () => number | null,
  // The current route name, or null.
  screen: () => string | null,
  send: (eventId: number, body: {events: TrackedClick[], app_version?: string}) => Promise<unknown>,
  appVersion?: string,
};

const FLUSH_MS = 20_000;
const FLUSH_JITTER = 0.3;
const MAX_QUEUED = 200;
// The API takes 50 events and 16 KB per batch. A click is about 200 bytes of ASCII, so its string length is
// its byte count; the margin is for the envelope.
const MAX_BATCH_EVENTS = 50;
const MAX_BATCH_BYTES = 15_000;
// The first send and one retry.
const MAX_ATTEMPTS = 2;

// Lead scanning, settings and the badge's own contact details are never tracked.
const EXCLUDED_SCREEN = /^(settings|events-eventId-leads.*|events-eventId-my-badge)$/;

const randomId = (): string => {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map(byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const isPositiveInt = (value: unknown): value is number => Number.isInteger(value) && (value as number) > 0;

let context: TrackingContext | null = null;
let queue: QueuedClick[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
// Bumped on logout: a send that fails afterwards must not put the old user's clicks back in the queue.
let generation = 0;
// Events that answered 403 (no ticket): nothing more is queued or sent for them until the page reloads.
const refusedEvents = new Set<number>();
// One per page load, so the clicks of one visit can be grouped.
let visit: string | null = null;

const clearTimer = () => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
};

const schedule = () => {
  if (timer || !queue.length) {
    return;
  }
  timer = setTimeout(() => {
    timer = null;
    void flushTracking();
  }, FLUSH_MS * (1 + Math.random() * FLUSH_JITTER));
};

// The queue is oldest first, so the cap drops the oldest. Retries go back in front of newer clicks.
const enqueue = (items: QueuedClick[], retry = false) => {
  queue = retry ? [...items, ...queue] : [...queue, ...items];
  if (queue.length > MAX_QUEUED) {
    queue = queue.slice(queue.length - MAX_QUEUED);
  }
  schedule();
};

const toBatches = (items: QueuedClick[]): QueuedClick[][] => {
  const batches: QueuedClick[][] = [];
  let batch: QueuedClick[] = [];
  let bytes = 0;
  for (const item of items) {
    const size = JSON.stringify(item.click).length + 1;
    if (batch.length && (batch.length >= MAX_BATCH_EVENTS || bytes + size > MAX_BATCH_BYTES)) {
      batches.push(batch);
      batch = [];
      bytes = 0;
    }
    batch.push(item);
    bytes += size;
  }
  if (batch.length) {
    batches.push(batch);
  }
  return batches;
};

const sendBatch = async (trackingContext: TrackingContext, eventId: number, batch: QueuedClick[], sentGeneration: number) => {
  try {
    await trackingContext.send(eventId, {
      events: batch.map(item => item.click),
      ...(trackingContext.appVersion ? {app_version: trackingContext.appVersion} : {}),
    });
  } catch (e) {
    const status = (e as {response?: {status?: number}})?.response?.status;
    if (status === 403) {
      refusedEvents.add(eventId);
    }
    // No answer at all, or a server error: once more with the next flush. A refusal (4xx) is dropped, the
    // same batch would only be refused again. The click ids do not change, so a batch the API stored before
    // the connection dropped is not counted twice.
    const retryable = status === undefined || status >= 500;
    const sameOwner = sentGeneration === generation && trackingContext.userId() === batch[0]!.userId;
    const again = batch.filter(item => item.attempts < MAX_ATTEMPTS);
    if (retryable && sameOwner && again.length) {
      enqueue(again, true);
    }
  }
};

export const configureTracking = (trackingContext: TrackingContext) => {
  context = trackingContext;
};

// Posts everything queued. The queue is taken whole before anything is sent, so flushes that overlap (the
// timer, the page being hidden, pagehide right after it) never send the same click twice.
export const flushTracking = async () => {
  clearTimer();
  const trackingContext = context;
  if (!trackingContext || !queue.length) {
    return;
  }
  const taken = queue;
  queue = [];

  const userId = trackingContext.userId();
  const byEvent = new Map<number, QueuedClick[]>();
  for (const item of taken) {
    // Logged out, or someone else logged in since: those clicks are not this user's to send.
    if (item.userId !== userId || refusedEvents.has(item.eventId)) {
      continue;
    }
    item.attempts++;
    const items = byEvent.get(item.eventId);
    if (items) {
      items.push(item);
    } else {
      byEvent.set(item.eventId, [item]);
    }
  }

  const sentGeneration = generation;
  const sends: Promise<void>[] = [];
  for (const [eventId, items] of byEvent) {
    for (const batch of toBatches(items)) {
      sends.push(sendBatch(trackingContext, eventId, batch, sentGeneration));
    }
  }
  await Promise.all(sends);
};

// Logout: everything queued belonged to the user who left.
export const resetTracking = () => {
  generation++;
  queue = [];
  clearTimer();
};

export const trackClick = (entity: TrackingEntity, action: TrackingAction, details: TrackingDetails = {}) => {
  if (!context) {
    return;
  }
  const userId = context.userId();
  const screen = context.screen();
  if (!userId || (screen && EXCLUDED_SCREEN.test(screen))) {
    return;
  }
  const eventId = details.eventId === undefined ? context.eventId() : details.eventId;
  if (!isPositiveInt(eventId) || refusedEvents.has(eventId)) {
    return;
  }

  visit ??= randomId();
  const click: TrackedClick = {id: randomId(), at: Date.now(), entity, action, visit};
  if (isPositiveInt(details.entityId)) {
    click.entity_id = details.entityId;
  }
  if (details.item) {
    click.item = details.item;
  }
  if (Number.isInteger(details.count) && details.count! >= 0) {
    click.count = details.count!;
  }
  if (screen) {
    click.screen = screen;
  }
  enqueue([{eventId, userId, click, attempts: 0}]);
};

export function useTracking() {
    const {$api} = useNuxtApp();

    // The older per-call tracking (POST /v2/tracking/event) of the ads, job cards and RSVP links.
    const trackEvent = async (type: string, data: TrackingData) => {
        const payload: TrackingPayload = {
            'event_type': type,
            'event_data': data
        }
        try {
            await $api.v2.trackEvent(payload);
        } catch (err) {
            console.log(err)
        }
    };

    return {
        trackEvent
    }
}
